import { useState } from 'react';
import { Clock, ChevronRight, Zap, TrendingUp, HardDrive, Leaf, Gauge, Shield } from 'lucide-react';

/**
 * AI Runtime Section - Infrastructure Behavior Under Real Traffic
 *
 * The core insight: Inference ROI isn't lost on model quality,
 * it's lost on infrastructure behavior under real traffic.
 *
 * Three behaviors that kill margins:
 * 1. Cold starts - GPUs aren't warm when traffic arrives
 * 2. Idle capacity - Over-provisioned for peaks, wasted during valleys
 * 3. Tail latency - P99s spike during load, breaking SLOs
 */

interface BehaviorCardProps {
    icon: React.ElementType;
    title: string;
    problem: string;
    impact: string;
    solution: string;
    isActive: boolean;
    onClick: () => void;
}

const BehaviorCard = ({
    icon: Icon,
    title,
    problem,
    impact,
    solution,
    isActive,
    onClick
}: BehaviorCardProps) => {
    return (
        <button
            onClick={onClick}
            className={`
                w-full text-left p-6 rounded-2xl border transition-all duration-300
                ${isActive
                    ? 'bg-white/[0.04] border-volt-500/30'
                    : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
                }
            `}
        >
            <div className="flex items-start justify-between mb-4">
                <div className={`
                    w-12 h-12 rounded-xl flex items-center justify-center
                    ${isActive ? 'bg-volt-500' : 'bg-white/[0.06]'}
                    transition-colors
                `}>
                    <Icon className={`w-6 h-6 ${isActive ? 'text-obsidian-900' : 'text-gray-400'}`} />
                </div>
                <ChevronRight className={`
                    w-5 h-5 transition-transform
                    ${isActive ? 'text-volt-400 rotate-90' : 'text-gray-600'}
                `} />
            </div>

            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-gray-400 text-sm mb-4">{problem}</p>

            {isActive && (
                <div className="space-y-4 pt-4 border-t border-white/10 animate-fade-in">
                    <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Impact</div>
                        <p className="text-sm text-gray-300">{impact}</p>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Forecaster Fix</div>
                        <p className="text-sm text-volt-400">{solution}</p>
                    </div>
                </div>
            )}
        </button>
    );
};

// CPU vs GPU Scaling Comparison
const ScalingComparison = () => {
    return (
        <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian-800 via-obsidian-700 to-obsidian-800 rounded-2xl" />

            <div className="relative p-6">
                {/* Header */}
                <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-white mb-1">
                        Why CPU Autoscaling ≠ GPU Scaling
                    </h3>
                    <p className="text-xs text-gray-500">HPA was designed for a different era</p>
                </div>

                {/* Two columns */}
                <div className="grid grid-cols-2 gap-4">
                    {/* CPU side - green/good */}
                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                        <div className="text-sm font-semibold text-emerald-400 mb-3">CPU Autoscaling</div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-emerald-400">✓</span>
                                <span className="text-gray-300">Fast starts</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-emerald-400">✓</span>
                                <span className="text-gray-300">Stateless pods</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-emerald-400">✓</span>
                                <span className="text-gray-300">Elastic scaling</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-emerald-400">✓</span>
                                <span className="text-gray-300">Cheap mistakes</span>
                            </div>
                        </div>
                    </div>

                    {/* GPU side - red/bad */}
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                        <div className="text-sm font-semibold text-red-400 mb-3">GPU Reality</div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-red-400">✗</span>
                                <span className="text-gray-300">30-60s cold starts</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-red-400">✗</span>
                                <span className="text-gray-300">Memory fragmented</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-red-400">✗</span>
                                <span className="text-gray-300">Pods stuck pending</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-red-400">✗</span>
                                <span className="text-gray-300">Idle $$ burn</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom insight */}
                <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-gray-400 text-center">
                        GPUs are <span className="text-white">scarce</span>, <span className="text-white">stateful</span>, <span className="text-white">slow to start</span>, and <span className="text-white">memory-coupled</span>.
                        <br />
                        Kubernetes autoscaling assumes the opposite.
                    </p>
                </div>
            </div>
        </div>
    );
};

export const AIRuntimeSection = () => {
    const [activeBehavior, setActiveBehavior] = useState(0);

    const behaviors = [
        {
            icon: Clock,
            title: 'Cold Starts',
            problem: "GPUs aren't warm when traffic arrives. First requests wait 10-60 seconds.",
            impact: 'P99 latency spikes during traffic bursts. Users abandon. Revenue lost.',
            solution: 'ML anticipates demand. Models stay warm and ready. GPUs ready before traffic arrives.',
        },
        {
            icon: TrendingUp,
            title: 'Low Utilization',
            problem: 'Average GPU utilization is 15-30%. Expensive hardware not earning its keep.',
            impact: 'Cost per inference is 3-4x what it should be. Margins compressed.',
            solution: '30% → 87% GPU utilization. Right-size in real-time. Multi-Region Intelligence finds the best capacity across 12 providers and 30+ regions. Spot arbitrage when SLO allows.',
        },
        {
            icon: HardDrive,
            title: 'KV Cache OOM',
            problem: 'Long conversations and concurrent users saturate GPU VRAM. The #1 production failure mode for LLM serving.',
            impact: 'OOM crashes, truncated context, degraded user experience. Buying bigger GPUs just shifts the cliff.',
            solution: 'Automatic KV cache tiering — VRAM → DRAM → NVMe → RDMA Network. Idle session offloading. Cache-aware routing sends returning users to GPUs with their state. Smart lifecycle beats more hardware.',
        },
        {
            icon: Leaf,
            title: 'Energy Waste',
            problem: 'A 700W H100 running decode at 10% utilization wastes energy. No visibility into Joules per inference.',
            impact: 'Energy costs grow linearly with fleet size. ESG reporting blind spot. Carbon footprint unknown.',
            solution: 'Energy-optimized routing. Each inference stage routes to the most energy-efficient accelerator. Carbon-aware routing across 18+ grid regions. Tokens-per-joule tracking per request.',
        },
    ];

    return (
        <section className="py-24 lg:py-32 bg-obsidian-900">
            <div className="max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 text-sm font-medium mb-6">
                        <Zap className="w-4 h-4" />
                        AI Runtime
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                        Memory, network, and coordination
                        <br />
                        <span className="text-gray-500">are killing your inference ROI.</span>
                    </h2>
                    <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                        KV cache OOM on long conversations. Cold starts. Memory bandwidth walls. Idle GPUs.
                        Inference is 15x more expensive than training — and most of that cost is infrastructure, not compute.
                    </p>
                </div>

                {/* Two column layout */}
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                    {/* Left: Behavior cards */}
                    <div className="space-y-4">
                        {behaviors.map((behavior, i) => (
                            <BehaviorCard
                                key={i}
                                {...behavior}
                                isActive={activeBehavior === i}
                                onClick={() => setActiveBehavior(i)}
                            />
                        ))}
                    </div>

                    {/* Right: CPU vs GPU comparison */}
                    <div className="lg:sticky lg:top-32 h-fit">
                        <ScalingComparison />

                        {/* Bottom insight */}
                        <div className="mt-8 p-6 rounded-xl bg-volt-500/5 border border-volt-500/20">
                            <p className="text-center text-white">
                                "Enterprises don't just need cheaper infrastructure —
                                <br />
                                <span className="text-volt-400 font-semibold">they need infrastructure that adapts automatically."</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Workload Classification */}
                <div className="mt-24">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 text-sm font-medium mb-6">
                            <Gauge className="w-4 h-4" />
                            Workload Intelligence
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">
                            Every workload is different.
                            <br />
                            <span className="text-gray-500">Forecaster knows the difference.</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                            Forecaster auto-classifies every request — from real-time voice to overnight batch jobs —
                            and routes each to the right GPU with the right SLO. No manual configuration.
                        </p>
                    </div>

                    {/* Online vs Offline */}
                    <div className="grid lg:grid-cols-2 gap-6 mb-12">
                        <div className="p-6 rounded-2xl border border-volt-500/20 bg-volt-500/[0.03]">
                            <div className="text-xs text-volt-400 uppercase tracking-wider font-medium mb-4">Online — Latency Optimized</div>
                            <p className="text-sm text-gray-400 mb-5">
                                Real-time applications with users waiting. Optimized for first-response speed and streaming smoothness.
                            </p>
                            <div className="space-y-3">
                                {[
                                    { type: 'Chat', example: 'Customer support with RAG', slo: 'Low latency' },
                                    { type: 'Agentic', example: 'Sales prospecting, tool calling', slo: 'Burst-tolerant' },
                                    { type: 'Realtime', example: 'Voice agents, live translation', slo: 'Ultra-low latency' },
                                    { type: 'Completion', example: 'Code completion in IDE', slo: 'Interactive' },
                                ].map((w) => (
                                    <div key={w.type} className="flex items-center justify-between p-3 rounded-lg bg-obsidian-800">
                                        <div>
                                            <span className="text-sm font-medium text-white">{w.type}</span>
                                            <span className="text-xs text-gray-500 ml-2">{w.example}</span>
                                        </div>
                                        <span className="text-xs text-volt-400 font-mono">{w.slo}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                            <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4">Offline — Throughput Optimized</div>
                            <p className="text-sm text-gray-400 mb-5">
                                Batch workloads where no user is waiting. Optimized for tokens per second across the fleet. Spot instances preferred.
                            </p>
                            <div className="space-y-3">
                                {[
                                    { type: 'Batch', example: 'Document processing, embeddings', slo: 'Cost priority' },
                                    { type: 'Moderation', example: 'Content safety scanning', slo: 'High throughput' },
                                    { type: 'Transcription', example: 'Catalog audio/video processing', slo: 'Deferrable' },
                                    { type: 'Corpus prep', example: 'Training data cleaning', slo: 'Off-peak' },
                                ].map((w) => (
                                    <div key={w.type} className="flex items-center justify-between p-3 rounded-lg bg-obsidian-800">
                                        <div>
                                            <span className="text-sm font-medium text-white">{w.type}</span>
                                            <span className="text-xs text-gray-500 ml-2">{w.example}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 font-mono">{w.slo}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* How classification works */}
                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                            <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center mb-4">
                                <Gauge className="w-5 h-5 text-volt-400" />
                            </div>
                            <h4 className="text-lg font-semibold text-white mb-2">Auto-Classification</h4>
                            <p className="text-sm text-gray-400">
                                Classifies from request characteristics — token count, latency budget, multi-turn patterns,
                                tool calls, burst detection. Or set explicitly via header.
                            </p>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                            <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center mb-4">
                                <Zap className="w-5 h-5 text-volt-400" />
                            </div>
                            <h4 className="text-lg font-semibold text-white mb-2">SLO-Driven Routing</h4>
                            <p className="text-sm text-gray-400">
                                Each workload type maps to an SLO tier. Forecaster tracks latency, throughput, and
                                end-to-end performance per GPU — and only routes to backends that can meet the SLO.
                            </p>
                        </div>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                            <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center mb-4">
                                <Shield className="w-5 h-5 text-volt-400" />
                            </div>
                            <h4 className="text-lg font-semibold text-white mb-2">Compliance Built In</h4>
                            <p className="text-sm text-gray-400">
                                Data sovereignty (EU, US, HIPAA), regulatory compliance (SOC2, ISO27001),
                                encryption requirements, and multi-tenancy isolation — enforced per request.
                            </p>
                        </div>
                    </div>

                    {/* Same model, two deployments insight */}
                    <div className="p-6 rounded-xl bg-gradient-to-r from-volt-500/10 via-volt-500/5 to-transparent border border-volt-500/20 text-center">
                        <p className="text-lg text-white font-medium mb-2">
                            Same model. Two deployments. Automatically.
                        </p>
                        <p className="text-gray-400 max-w-3xl mx-auto">
                            A Whisper model serving real-time dictation and batch transcription doesn't need manual tuning.
                            Forecaster creates latency-optimized and throughput-optimized deployments from one config —
                            and routes each request to the right one.
                        </p>
                    </div>
                </div>

                {/* Section divider */}
                <div className="max-w-xl mx-auto mt-16">
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
            </div>
        </section>
    );
};
